import React from 'react';
import { Box, Typography, Tooltip } from '@material-ui/core';
import { useTimelineViewStyles } from '../styles/timeline-view.styles';

const ENTER_TOUCH_DELAY = 0;
const LEAVE_TOUCH_DELAY = 1500;

/**
 * Props interface for the GenericTooltip component
 */
interface GenericTooltipProps {
  /** Title to display in the tooltip */
  title: string;
  /** Description to display in the tooltip */
  description?: string;
  /** Child element that triggers the tooltip */
  children: React.ReactElement;
  /** Tooltip placement */
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end' | 'left-start' | 'left-end' | 'right-start' | 'right-end';
  /** Whether to show arrow */
  arrow?: boolean;
}

/**
 * GenericTooltip Component
 * 
 * A reusable tooltip component that displays title and description with
 * consistent styling matching the milestone and timeline components.
 * Wraps any child component and shows tooltip on hover.
 * 
 * @component
 * @param props - The component props
 * @param props.title - Title text to display in tooltip
 * @param props.description - Optional description text to display below title
 * @param props.children - Element that triggers the tooltip on hover
 * @param props.placement - Tooltip placement position (default: 'top')
 * @param props.arrow - Whether to show tooltip arrow (default: true)
 * 
 * @returns A tooltip wrapper with consistent styling
 */
export const GenericTooltip = React.memo(({ 
  title, 
  description, 
  children, 
  placement = 'top',
  arrow = true 
}: GenericTooltipProps) => {
  const classes = useTimelineViewStyles();
  
  return (
    <Tooltip
      title={
        <Box className={classes.tooltipContent}>
          <Typography variant="subtitle2" className={classes.tooltipTitle}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" style={{ marginTop: 4, color: 'rgba(255,255,255,0.9)' }} className={classes.tooltipContent}>
              {description}
            </Typography>
          )}
        </Box>
      }
      placement={placement}
      arrow={arrow}
      enterTouchDelay={ENTER_TOUCH_DELAY}
      leaveTouchDelay={LEAVE_TOUCH_DELAY}
      disableFocusListener={false}
      disableTouchListener={false}
    >
      {children}
    </Tooltip>
  );
});

GenericTooltip.displayName = 'GenericTooltip';
